// Probe de performance DEV-ONLY para cazar los freezes del hilo principal (long tasks) durante el
// pase login → juego y la ventana de protección de aparición. NO se activa en prod.
//
// Uso: al reproducir el cuelgue, abrir la consola del navegador (F12) y pasar las líneas `[PERF]`,
// o correr `copy(window.__perfDump())` para copiar el resumen ordenado. Cada línea dice cuántos ms
// bloqueó el hilo y en qué FASE (login / entrar / ingame / protección) ocurrió, que es lo que hace
// falta para atacar el bloque exacto. Ver docs/PERFORMANCE_OPTIMIZATION.md.

interface PerfEntry {
  t: number; // startTime (ms desde navigation start)
  d: number; // duración del long task (ms)
  phase: string; // fase en la que cayó
}

let currentPhase = 'boot';
let installed = false;
const entries: PerfEntry[] = [];

/**
 * Umbral (ms): sólo registramos tasks que bloquean al menos esto. Un frame a 60fps = 16.7ms.
 * Es AJUSTABLE en caliente (sin rebuild):
 *   - `?perf=80` en la URL o `localStorage.perf = '80'` lo fijan al abrir.
 *   - `window.__perfThreshold(80)` lo cambia en el momento desde la consola.
 * Subilo para filtrar el ruido de tasks chicos recurrentes y ver sólo los freezes grandes.
 */
let thresholdMs = 40;

/** Lee un override numérico del umbral desde `?perf=NN` o `localStorage.perf` (si es número). */
function readThresholdOverride(): number | null {
  try {
    if (typeof location !== 'undefined') {
      const m = /[?&]perf=(\d+)/.exec(location.search);
      if (m) return Number(m[1]);
    }
    if (typeof localStorage !== 'undefined') {
      const v = localStorage.getItem('perf');
      if (v != null && /^\d+$/.test(v.trim())) return Number(v.trim());
    }
  } catch {
    // acceso a location/localStorage puede fallar en contextos raros → sin override.
  }
  return null;
}

/** Cambia la fase actual; los long tasks siguientes se etiquetan con ella. */
export function setPerfPhase(name: string): void {
  currentPhase = name;
  if (installed) console.warn(`[PERF] --- fase: ${name} ---`);
}

/**
 * ¿Hay que instalar el probe? En DEV siempre. En prod SOLO si se opta explícitamente vía
 * `?perf` en la URL o `localStorage.perf` (así se puede cazar un freeze en aureum.cc sin
 * dejar el observer prendido para todos). El observer sólo loguea tasks >= `thresholdMs`, así
 * que el costo es despreciable, pero lo mantenemos opt-in en prod por prolijidad.
 */
function shouldInstall(): boolean {
  if (import.meta.env.DEV) return true;
  try {
    if (typeof location !== 'undefined' && /[?&]perf\b/.test(location.search)) return true;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('perf') != null) return true;
  } catch {
    // acceso a location/localStorage puede fallar en contextos raros → sin probe.
  }
  return false;
}

/** Instala el observer de long tasks (idempotente). DEV siempre; prod con `?perf`/localStorage. */
export function installPerfProbe(): void {
  if (installed) return;
  if (!shouldInstall()) return;
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const obs = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.duration < thresholdMs) continue;
        const entry: PerfEntry = { t: Math.round(e.startTime), d: Math.round(e.duration), phase: currentPhase };
        entries.push(entry);
        console.warn(`[PERF] freeze ${entry.d}ms @ ${entry.phase} (t=${entry.t}ms)`);
      }
    });
    obs.observe({ type: 'longtask', buffered: true });
    installed = true;

    // Aplicar override de umbral (URL/localStorage) al instalar.
    const override = readThresholdOverride();
    if (override != null) thresholdMs = override;

    // Setter en caliente: `window.__perfThreshold(80)` filtra a >=80ms sin reload.
    (window as unknown as { __perfThreshold?: (ms: number) => string }).__perfThreshold = (
      ms: number
    ): string => {
      thresholdMs = Math.max(0, Math.floor(ms));
      return `[PERF] umbral = ${thresholdMs}ms`;
    };
    // Exponer un dump ordenado para copiar de una.
    (window as unknown as { __perfDump?: () => string }).__perfDump = (): string => {
      const sorted = [...entries].sort((a, b) => b.d - a.d);
      const total = entries.reduce((s, e) => s + e.d, 0);
      const byPhase: Record<string, number> = {};
      for (const e of entries) byPhase[e.phase] = (byPhase[e.phase] ?? 0) + e.d;
      return JSON.stringify({ totalBlockingMs: total, byPhase, worst: sorted.slice(0, 20) }, null, 2);
    };
    console.warn(
      `[PERF] probe instalado (umbral ${thresholdMs}ms) — subilo con __perfThreshold(80) o ?perf=80. ` +
        'Reproducí el cuelgue y pasá las líneas [PERF] (o copy(window.__perfDump())).'
    );
  } catch {
    // Si el browser no soporta 'longtask', no pasa nada.
  }
}
